
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Shirt, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { formatTShirtSize } from '@/utils/formatUtils';
import { useIsMobile } from '@/hooks/use-mobile';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

interface TShirtSizeModalProps {
  open: boolean;
  onClose: () => void;
  attendeeName: string;
  tShirtSize: string | undefined;
  autoCloseDelay?: number; // in milliseconds
}

const TShirtSizeModal: React.FC<TShirtSizeModalProps> = ({
  open,
  onClose,
  attendeeName,
  tShirtSize,
  autoCloseDelay = 5000 // Default to 5 seconds for better visibility
}) => {
  const isMobile = useIsMobile();
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const isClosingRef = useRef<boolean>(false);
  
  // Log raw t-shirt size for debugging
  console.log(`TShirtSizeModal received raw size: "${tShirtSize}"`);
  
  // Format the t-shirt size
  const formattedSize = formatTShirtSize(tShirtSize || '');
  console.log(`TShirtSizeModal formatted size: "${formattedSize}"`);
  
  // Determine gender-based styling
  const isMale = formattedSize.includes('(Nam)');
  const isFemale = formattedSize.includes('(Nữ)');
  
  const bgColorClass = isMale 
    ? 'bg-blue-50 dark:bg-blue-950/30' 
    : isFemale 
      ? 'bg-pink-50 dark:bg-pink-950/30'
      : 'bg-slate-50 dark:bg-slate-900/50';
      
  const textColorClass = isMale 
    ? 'text-blue-700 dark:text-blue-300' 
    : isFemale 
      ? 'text-pink-700 dark:text-pink-300'
      : 'text-slate-700 dark:text-slate-300';
  
  // Sync internal open state with prop
  useEffect(() => {
    if (open) {
      setIsOpen(true);
      isClosingRef.current = false;
    }
  }, [open]);

  // Handle actual close with debounce
  const handleClose = React.useCallback(() => {
    if (isClosingRef.current) {
      console.log('Already closing modal, ignoring duplicate close attempt');
      return;
    }
    
    // Mark as closing to prevent multiple calls
    isClosingRef.current = true;
    console.log('Closing T-shirt modal (single handler)');
    
    // Clear any auto-close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    
    // First update our internal state
    setIsOpen(false);
    
    // Then call the parent onClose with a small delay
    // This delay helps avoid race conditions with React's rendering
    setTimeout(() => {
      onClose();
      
      // Reset closing flag after a delay
      setTimeout(() => {
        isClosingRef.current = false;
      }, 300);
    }, 10);
  }, [onClose]);
  
  // Set up auto-close timer only once when modal opens
  useEffect(() => {
    if (isOpen && !closeTimeoutRef.current) {
      closeTimeoutRef.current = setTimeout(() => {
        console.log('Auto-closing T-shirt modal after timeout');
        handleClose();
      }, autoCloseDelay);
    }
    
    return () => {
      // Clear timeout on unmount or when open changes
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, [isOpen, autoCloseDelay, handleClose]);
  
  // Vibrate device on open (for mobile devices)
  useEffect(() => {
    if (isOpen && isMobile && 'vibrate' in navigator) {
      navigator.vibrate([100, 50, 100]);
    }
  }, [isOpen, isMobile]);

  // Emergency close handler with keyboard shortcut (Escape key)
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !isClosingRef.current) {
        console.log('Emergency closing T-shirt modal with Escape key');
        handleClose();
      }
    };
    
    // Add event listener for Escape key
    window.addEventListener('keydown', handleEscapeKey);
    
    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, handleClose]);

  return (
    <Dialog 
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <DialogContent 
        className={`p-0 overflow-hidden ${isMobile ? 'max-w-[90%] w-[330px]' : 'max-w-sm'} border-0 shadow-lg z-50`}
        onEscapeKeyDown={handleClose}
        onPointerDownOutside={handleClose}
        onInteractOutside={handleClose}
      >
        {/* Accessibility elements - now using proper Dialog components */}
        <VisuallyHidden asChild>
          <DialogTitle>Thông tin size áo</DialogTitle>
        </VisuallyHidden>
        <VisuallyHidden asChild>
          <DialogDescription id="t-shirt-modal-description">
            Size áo của {attendeeName}: {formattedSize}
          </DialogDescription>
        </VisuallyHidden>
        
        <motion.div
          className="flex flex-col items-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
        >
          {/* Success indicator */}
          <div className="w-full p-4 bg-green-500 flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className="text-white h-8 w-8 mr-2" />
              <h2 className="text-xl font-semibold text-white">Đã điểm danh</h2>
            </div>
            <button 
              className="rounded-full p-2 bg-green-400/30 text-white hover:bg-green-600/50"
              onClick={handleClose}
              aria-label="Đóng"
            >
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.7816 4.03157C12.0062 3.80702 12.0062 3.44295 11.7816 3.2184C11.5571 2.99385 11.193 2.99385 10.9685 3.2184L7.50005 6.68682L4.03164 3.2184C3.80708 2.99385 3.44301 2.99385 3.21846 3.2184C2.99391 3.44295 2.99391 3.80702 3.21846 4.03157L6.68688 7.49999L3.21846 10.9684C2.99391 11.193 2.99391 11.557 3.21846 11.7816C3.44301 12.0061 3.80708 12.0061 4.03164 11.7816L7.50005 8.31316L10.9685 11.7816C11.193 12.0061 11.5571 12.0061 11.7816 11.7816C12.0062 11.557 12.0062 11.193 11.7816 10.9684L8.31322 7.49999L11.7816 4.03157Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
              </svg>
            </button>
          </div>
          
          {/* Attendee info */}
          <div className="px-4 py-3 w-full bg-white dark:bg-gray-800">
            <p className="text-center text-lg font-medium truncate max-w-full">
              {attendeeName}
            </p>
          </div>
          
          {/* T-shirt size display */}
          <div aria-describedby="t-shirt-modal-description" className={`w-full p-8 ${bgColorClass} flex flex-col items-center justify-center`}>
            <Shirt className={`h-16 w-16 mb-2 ${textColorClass}`} />
            <p className="text-sm text-center text-gray-500 dark:text-gray-400 mb-1">
              Cỡ áo:
            </p>
            <p className={`text-3xl font-bold ${textColorClass}`}>
              {formattedSize}
            </p>
          </div>
          
          {/* Progress bar for auto-close visualization */}
          <div className="w-full bg-gray-100 dark:bg-gray-700 h-1 relative">
            <motion.div 
              className="absolute top-0 left-0 h-full bg-green-500"
              initial={{ width: '100%' }}
              animate={{ width: '0%' }}
              transition={{ duration: autoCloseDelay / 1000, ease: 'linear' }}
            />
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default TShirtSizeModal;
